import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { BAND_LABEL_ID_BY_TONE, EMPTY_VALUE, RECOMMENDATIONS_ANCHOR, TONE } from '../../data/constants'
import { getEntitiesFlaggedCount, getFullDeploymentHours } from '../../data/reportModel'
import { useAIRiskReportExportMode } from '../../components/RenderMode/AIRiskReportRenderModeProvider'
import styles from './OverviewHero.module.scss'

function ScoreValue({ score, tone, className = '' }) {
  return <div className={`${styles.scoreValue} ${styles[tone] ?? ''} ${className}`}>{score ?? EMPTY_VALUE}</div>
}

ScoreValue.propTypes = {
  score: PropTypes.number,
  tone: PropTypes.oneOf(Object.values(TONE)).isRequired,
  className: PropTypes.string,
}

export default function OverviewHero({
  overall = null,
  overallTone = TONE.NEUTRAL,
  projected = null,
  projectedTone = TONE.NEUTRAL,
  projectedWithLicenses = null,
  showLicensedProjection = false,
  summary = {},
  recommendationCount = 0,
  findings = [],
}) {
  const isExportMode = useAIRiskReportExportMode()
  const score = overall
  const tone = overallTone
  // The projected posture is the ceiling with the tenant's current licensing: resolving every
  // recommendation lifts the assessable areas to full coverage, but license-gated areas stay at 0.
  const projectedScore = projected
  const shouldShowLicensedProjection =
    showLicensedProjection && Number.isFinite(projectedWithLicenses) && projectedWithLicenses !== projectedScore
  const fullDeploymentHours = getFullDeploymentHours(summary)
  const recommendationTileClassName = [
    styles.smallTile,
    !isExportMode ? styles.smallTileLink : '',
    'd-flex flex-column gap-2',
  ]
    .filter(Boolean)
    .join(' ')
  const recommendationTileContent = (
    <>
      <div className={styles.tileLabel}>
        <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.RECOMMENDATIONS_TO_ADDRESS" />
      </div>
      <div className={`${styles.bigNumber} ${styles.info} mt-auto`}>{recommendationCount}</div>
    </>
  )

  return (
    <div className={`${styles.grid} d-grid`}>
      <div className={`${styles.overallTile} ${styles[tone] ?? ''} d-flex flex-column gap-3`}>
        <div className={styles.tileLabel}>
          <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.OVERALL_POSTURE" />
        </div>
        <div>
          <div className={styles.overallScore}>
            {score ?? EMPTY_VALUE}
            {score !== null && <small>%</small>}
          </div>
          <div className={styles.bandLabel}>
            <FormattedMessage id={BAND_LABEL_ID_BY_TONE[tone]} />
          </div>
        </div>
        <div className={`${styles.legend} d-flex flex-wrap mt-auto`}>
          <span className="d-inline-flex align-items-center gap-1">
            <i className={styles.ok} /> <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.LEGEND_STRONG" />
          </span>
          <span className="d-inline-flex align-items-center gap-1">
            <i className={styles.warn} /> <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.LEGEND_MODERATE" />
          </span>
          <span className="d-inline-flex align-items-center gap-1">
            <i className={styles.danger} /> <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.LEGEND_GAPS" />
          </span>
        </div>
      </div>

      {isExportMode ? (
        <div className={recommendationTileClassName}>{recommendationTileContent}</div>
      ) : (
        <a href={`#${RECOMMENDATIONS_ANCHOR}`} className={recommendationTileClassName}>
          {recommendationTileContent}
        </a>
      )}

      <div className={`${styles.smallTile} d-flex flex-column gap-2`}>
        <div className={styles.tileLabel}>
          <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.AFTER_RECOMMENDATIONS" />
        </div>
        <div className={`${styles.projectedFlow} d-flex align-items-center mt-auto`}>
          <ScoreValue score={score} tone={tone} />
          <span className={styles.projectedArrow} aria-hidden="true">
            →
          </span>
          <ScoreValue score={projectedScore} tone={projectedTone} />
          {shouldShowLicensedProjection && (
            <span className={styles.licensedProjection}>
              <FormattedMessage
                id="AI_RISK_REPORT.OVERVIEW.WITH_REQUIRED_LICENSES"
                values={{ score: projectedWithLicenses }}
              />
            </span>
          )}
        </div>
      </div>

      <div className={`${styles.smallTile} d-flex flex-column gap-2`}>
        <div className={styles.tileLabel}>
          <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.ENTITIES_FLAGGED" />
        </div>
        <div className={`${styles.bigNumber} mt-auto`}>{getEntitiesFlaggedCount(findings)}</div>
      </div>

      <div className={`${styles.smallTile} d-flex flex-column gap-2`}>
        <div className={styles.tileLabel}>
          <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.PRODUCTIVITY_CAPACITY" />
        </div>
        <div className={`${styles.bigNumber} mt-auto`}>
          {fullDeploymentHours > 0 ? `~${fullDeploymentHours}` : EMPTY_VALUE}{' '}
          {fullDeploymentHours > 0 && (
            <small>
              <FormattedMessage id="AI_RISK_REPORT.OVERVIEW.HOURS_PER_WEEK" />
            </small>
          )}
        </div>
      </div>
    </div>
  )
}

OverviewHero.propTypes = {
  overall: PropTypes.number,
  overallTone: PropTypes.oneOf(Object.values(TONE)),
  projected: PropTypes.number,
  projectedTone: PropTypes.oneOf(Object.values(TONE)),
  projectedWithLicenses: PropTypes.number,
  showLicensedProjection: PropTypes.bool,
  summary: PropTypes.shape({}),
  recommendationCount: PropTypes.number,
  findings: PropTypes.arrayOf(PropTypes.shape({})),
}
